'use client';

import { cn } from '@/lib/utils';
import styles from './document-card.module.scss';

export type DocumentCardProps = {
  fileSrc: string;
  fileName: string;
  fileType?: string | null;
  fileSize?: number | null;
  description?: string | null;
  className?: string;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DocumentCard = ({
  fileSrc,
  fileName,
  fileType,
  fileSize,
  description,
  className,
}: DocumentCardProps) => {
  const badge = fileType?.toUpperCase() ?? 'FILE';

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    fetch(fileSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <a
      href={fileSrc}
      download={fileName}
      onClick={handleClick}
      className={cn(styles['document-card'], className)}
    >
      <span className={styles['document-card__badge']}>{badge}</span>
      <span className={styles['document-card__info']}>
        <span className={styles['document-card__name']}>{fileName}</span>
        {fileSize != null && (
          <span className={styles['document-card__size']}>{formatFileSize(fileSize)}</span>
        )}
        {description && <span className={styles['document-card__description']}>{description}</span>}
      </span>
      <span className={styles['document-card__icon']} aria-hidden="true">
        ↓
      </span>
    </a>
  );
};
